/* eslint-disable sonarjs/cognitive-complexity */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { scroller } from 'react-scroll';
import {
  GIVE_AS_A_GIFT,
  CHOOSE_YOUR_OPTIONS,
  BUYNOW,
  NIBBLE_TRACKING_FLAG,
} from '../../../../config/text/text';
import { trackEvent } from '../../../../helpers/analytics';
import { getIsDropdownDeal, getIsLocalDeal } from '../../../../helpers/deals';
import { getScreenBreakpoint } from '../../../../helpers/screen';
import ThemeContext from '../../../../providers/ThemeProvider';
import WowModal from '../../../WowModal';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import { isNibbleEnabledDealLevel } from '../../../nibble/NibbleHelper';
import DealChooseYourOptionsModal from './DealChooseYourOptionsModal';
import SecondCheckoutChooseYourOptionsModal from './SecondCheckoutChooseYourOptionsModal';

const DealChooseYourOptionsButton = ({
  buttonRef,
  deal = {},
  className = '',
  stickyButton = false,
  handleBuyButtonClick,
  isTypeMobile = false,
  pageDetails = {},
  targetScroll = '',
  isOptionSelected = false,
  secondCheckout = false,
  finePrintCollapsed,
  setFinePrintCollapsed,
  setFullDetailsCollapsed,
  fullDetailsCollapsed,
  locationCollapsed,
  isModernVipVariant = false,
  setLocationCollapsed,
  onlyGift = false,
  isAuthenticated = false,
  isExpressDealPage = false,
  buyButton = null,
  selectedOptions,
  paymentData = null,
  setIsCYOSelected,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const theme = useContext(ThemeContext);
  const userVipStatus = useSelector(
    (state) => state.user.userprofile?.userVipStatus,
  );
  const onClick = () => {
    if (localStorage.getItem(NIBBLE_TRACKING_FLAG) !== 'true') {
      trackEvent('click_buy');
    }
    setIsVisible(getIsDropdownDeal(deal) && getIsLocalDeal(deal));
    if (pageDetails?.isTravel && !isTypeMobile) {
      if (isOptionSelected) {
        buttonRef?.current?.click();

        return;
      }
      const breakpoint = getScreenBreakpoint();
      scroller.scrollTo(targetScroll, {
        delay: 100,
        duration: 1_000,
        offset: breakpoint === 'sm' ? -300 : 0,
        smooth: true,
      });
    } else {
      setShowModal(true);
    }
  };

  const enforceFocus = !isNibbleEnabledDealLevel(deal, userVipStatus);

  return (
    <>
      <div
        className={classNames('buy-button', {
          'hide-desktop': isTypeMobile,
          'maindeal-combined-button sticky-button': stickyButton,
        })}
      >
        {/* the span inside the button is only for GOOGLE purposes */}
        {!onlyGift && (
          <PrimaryButton
            aria-label={secondCheckout ? BUYNOW : CHOOSE_YOUR_OPTIONS}
            buttonRef={isOptionSelected ? null : buttonRef}
            /* eslint-disable-next-line react/forbid-component-props */
            className={classNames('choose-your-options-button', className)}
            id="choose-your-options"
            onClick={onClick}
          >
            {secondCheckout ? BUYNOW : `${CHOOSE_YOUR_OPTIONS}!`}

            <span style={{ display: 'none' }}>buy</span>
          </PrimaryButton>
        )}

        {!pageDetails.isTravel && deal.giftable && !secondCheckout && (
          <div
            className={classNames('gift-button', {
              'hide-desktop': isTypeMobile,
            })}
          >
            <PrimaryButton
              aria-label={GIVE_AS_A_GIFT}
              /* eslint-disable-next-line react/forbid-component-props */
              className={onlyGift ? 'gifting' : 'full-width outline margin'}
              onClick={onClick}
            >
              {GIVE_AS_A_GIFT}
            </PrimaryButton>
          </div>
        )}
      </div>

      <WowModal
        ariaLabelledBy="choose-your-options"
        centered
        enforceFocus={enforceFocus}
        extraClasses="choose-your-options"
        setShowModal={setShowModal}
        showModal={showModal}
      >
        {secondCheckout ? (
          <SecondCheckoutChooseYourOptionsModal
            deal={deal}
            finePrintCollapsed={finePrintCollapsed}
            fullDetailsCollapsed={fullDetailsCollapsed}
            handleBuyButtonClick={handleBuyButtonClick}
            locationCollapsed={locationCollapsed}
            secondCheckout={secondCheckout}
            setFinePrintCollapsed={setFinePrintCollapsed}
            setFullDetailsCollapsed={setFullDetailsCollapsed}
            setLocationCollapsed={setLocationCollapsed}
          />
        ) : (
          <DealChooseYourOptionsModal
            buyButton={buyButton}
            deal={deal}
            finePrintCollapsed={finePrintCollapsed}
            handleBuyButtonClick={handleBuyButtonClick}
            isAuthenticated={isAuthenticated}
            isExpressDealPage={isExpressDealPage}
            isModernVipVariant={isModernVipVariant}
            isTypeMobile={isTypeMobile}
            isVisible={isVisible}
            paymentData={paymentData}
            secondCheckout={secondCheckout}
            selectedOptions={selectedOptions}
            setFinePrintCollapsed={setFinePrintCollapsed}
            setIsCYOSelected={setIsCYOSelected}
          />
        )}
      </WowModal>
      <style jsx>{`
        .express-checkout-text {
          background-color: white;
          font-size: 10px;
          padding: 0px 4px 0 10px;
          margin-bottom: 50px;
          margin-top: -8px;
        }
        :global(.cat-main-deal .cat-main-deal__header-buttons .gift-button) {
          display: none;
        }
        :global(.choose-your-options .custom-select__select) {
          font-size: 20px;
        }
        :global(.primary-button.gifting) {
          color: ${theme.colors.primary} !important;
          background-color: #ffffff;
          border: 3px solid ${theme.colors.primary} !important;
          width: 75%;
          margin-left: 15%;
          transform: translate(-5%, -80%);
          margin-bottom: 0px;
          font-weight: bold;
        }
      `}</style>
    </>
  );
};

DealChooseYourOptionsButton.propTypes = {
  buttonRef: PropTypes.object,
  className: PropTypes.string,
  deal: PropTypes.object,
  finePrintCollapsed: PropTypes.bool,
  fullDetailsCollapsed: PropTypes.bool,
  handleBuyButtonClick: PropTypes.func,
  isModernVipVariant: PropTypes.bool,
  isOptionSelected: PropTypes.bool,
  isTypeMobile: PropTypes.bool,
  locationCollapsed: PropTypes.bool,
  onlyGift: PropTypes.bool,
  pageDetails: PropTypes.any,
  secondCheckout: PropTypes.bool,
  setFinePrintCollapsed: PropTypes.func,
  setFullDetailsCollapsed: PropTypes.func,
  setIsCYOSelected: PropTypes.func,
  setLocationCollapsed: PropTypes.func,
  stickyButton: PropTypes.bool,
  targetScroll: PropTypes.any,
};

export default DealChooseYourOptionsButton;
